/**
 * GanttView — timeline visualisation of cards with optional dependency
 * arrows + drag-to-reschedule (Phase 12 / M10).
 *
 * Layout pipeline:
 *   computeGanttLayout()  →  scale + bars + arrows
 *
 * Interactions:
 *   - Click a bar → onOpenCard(id).
 *   - Drag a bar horizontally → onReschedule(id, newDueISO).
 *   - Zoom controls flip the scale (day / week / month).
 *   - Critical-path toggle highlights the longest dependency chain.
 *
 * Strict monochrome chrome (ADR-057). The only colour outside the
 * grayscale palette is the today-line (status.warning yellow) and the
 * critical-path bar/arrow (status.error red).
 */
import type { Card } from '@chaos/types/kanban';
import type { CardDependency, GanttScale } from '@chaos/types/views';
import { Inbox } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../../lib/utils';
import {
  computeCriticalPath,
  computeGanttLayout,
  type GanttBarLayout,
  pxPerDay,
} from './gantt-layout';

const ROW_HEIGHT = 28;
const SCALE_HEIGHT = 28;

export interface GanttViewProps {
  cards: Card[];
  dependencies?: CardDependency[];
  isLoading?: boolean;
  onOpenCard?: (id: string) => void;
  /** Drag commit callback; receives the new due-date ISO. */
  onReschedule?: (cardId: string, newDueISO: string) => void;
  initialScale?: GanttScale;
  onSettingsChange?: (settings: { scale: GanttScale; showCriticalPath: boolean }) => void;
  className?: string;
}

interface DragState {
  cardId: string;
  startX: number;
  startBarX: number;
  pxPerDay: number;
  endISO: string;
}

export function GanttView({
  cards,
  dependencies = [],
  isLoading,
  onOpenCard,
  onReschedule,
  initialScale = 'week',
  onSettingsChange,
  className,
}: GanttViewProps) {
  const [scale, setScale] = React.useState<GanttScale>(initialScale);
  const [showCritical, setShowCritical] = React.useState(false);

  React.useEffect(() => {
    onSettingsChange?.({ scale, showCriticalPath: showCritical });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale, showCritical, onSettingsChange]);

  const layout = React.useMemo(
    () => computeGanttLayout({ cards, dependencies, scale, rowHeight: ROW_HEIGHT }),
    [cards, dependencies, scale],
  );

  const critical = React.useMemo(
    () => (showCritical ? computeCriticalPath(cards, dependencies) : new Set<string>()),
    [showCritical, cards, dependencies],
  );

  const todayX = React.useMemo(() => {
    const now = new Date();
    if (now < layout.rangeStart || now > layout.rangeEnd) return null;
    const dayMs = 24 * 60 * 60 * 1000;
    const offsetDays = Math.round((now.getTime() - layout.rangeStart.getTime()) / dayMs);
    return offsetDays * pxPerDay(scale);
  }, [layout, scale]);

  // Drag state (kept in ref for high-frequency mousemove).
  const dragRef = React.useRef<DragState | null>(null);
  const [dragPreview, setDragPreview] = React.useState<{
    cardId: string;
    deltaPx: number;
  } | null>(null);

  const handleMouseDown = React.useCallback(
    (e: React.MouseEvent, bar: GanttBarLayout) => {
      e.stopPropagation();
      if (!onReschedule) return;
      dragRef.current = {
        cardId: bar.card.id,
        startX: e.clientX,
        startBarX: bar.x,
        pxPerDay: pxPerDay(scale),
        endISO: bar.endISO,
      };
      setDragPreview({ cardId: bar.card.id, deltaPx: 0 });

      function move(ev: MouseEvent) {
        const drag = dragRef.current;
        if (!drag) return;
        const delta = ev.clientX - drag.startX;
        setDragPreview({ cardId: drag.cardId, deltaPx: delta });
      }
      function up(ev: MouseEvent) {
        const drag = dragRef.current;
        dragRef.current = null;
        if (!drag) return;
        const delta = ev.clientX - drag.startX;
        const dayMs = 24 * 60 * 60 * 1000;
        const deltaDays = Math.round(delta / drag.pxPerDay);
        if (deltaDays !== 0) {
          const original = new Date(drag.endISO).getTime();
          const next = new Date(original + deltaDays * dayMs).toISOString();
          onReschedule?.(drag.cardId, next);
        }
        setDragPreview(null);
        window.removeEventListener('mousemove', move);
        window.removeEventListener('mouseup', up);
      }
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', up);
    },
    [onReschedule, scale],
  );

  const isEmpty = !isLoading && cards.length === 0;
  const chartHeight = layout.height;

  return (
    <section
      className={cn(
        'flex h-full min-h-0 flex-col overflow-hidden border border-border bg-background',
        className,
      )}
      aria-label="Gantt timeline view"
    >
      <header className="flex items-center justify-between gap-2 border-b border-border bg-background px-3 py-2">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium text-foreground">{cards.length} cards</span>
          <span className="text-2xs text-muted-foreground">
            {layout.rangeStart.toLocaleDateString()} → {layout.rangeEnd.toLocaleDateString()}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={showCritical}
              onChange={(e) => setShowCritical(e.target.checked)}
              className="h-3.5 w-3.5 accent-foreground"
            />
            Critical path {showCritical && critical.size > 0 && `(${critical.size})`}
          </label>
          <ZoomControls value={scale} onChange={setScale} />
        </div>
      </header>

      <div
        role="region"
        aria-label="Gantt chart"
        aria-busy={Boolean(isLoading)}
        className="relative flex-1 overflow-auto bg-background"
      >
        {isEmpty ? (
          <div className="flex h-full flex-col items-center justify-center gap-1 p-8 text-center text-muted-foreground">
            <Inbox size={20} aria-hidden />
            <p className="text-sm">No cards in range.</p>
          </div>
        ) : (
          <div
            className="relative"
            style={{
              width: layout.width,
              minHeight: chartHeight + SCALE_HEIGHT,
            }}
          >
            {/* Time-scale ticks */}
            <div
              className="sticky top-0 z-10 border-b border-border bg-background"
              style={{ height: SCALE_HEIGHT, width: layout.width }}
            >
              {layout.ticks.map((t) => (
                <div
                  key={t.iso}
                  className="absolute top-0 flex h-full items-center pl-1 text-2xs text-muted-foreground"
                  style={{ left: t.x }}
                >
                  <span>{t.label}</span>
                </div>
              ))}
            </div>

            {/* Row backgrounds */}
            <div
              aria-hidden
              className="absolute left-0 right-0"
              style={{ top: SCALE_HEIGHT, height: chartHeight }}
            >
              {layout.bars.map((bar, i) => (
                <div
                  key={`bg-${bar.card.id}`}
                  className={cn(
                    'absolute left-0 right-0 border-b border-border/60',
                    i % 2 === 0 ? 'bg-background' : 'bg-foreground/[0.02]',
                  )}
                  style={{ top: i * ROW_HEIGHT, height: ROW_HEIGHT }}
                />
              ))}
            </div>

            {/* Bars */}
            <div className="relative" style={{ top: 0, height: chartHeight }}>
              {layout.bars.map((bar) => {
                const onCritical = critical.has(bar.card.id);
                const previewDelta =
                  dragPreview && dragPreview.cardId === bar.card.id ? dragPreview.deltaPx : 0;
                return (
                  <button
                    type="button"
                    key={bar.card.id}
                    onMouseDown={(e) => handleMouseDown(e, bar)}
                    onClick={() => onOpenCard?.(bar.card.id)}
                    className={cn(
                      'absolute flex items-center rounded-sm border border-border bg-foreground/[0.06] px-2 text-xs text-foreground transition-shadow',
                      'cursor-grab hover:bg-foreground/[0.1] active:cursor-grabbing',
                      onCritical &&
                        'border-rose-500/60 bg-rose-500/15 text-rose-700 dark:text-rose-300',
                      previewDelta !== 0 && 'shadow-[0_0_0_1px_rgba(0,0,0,0.2)]',
                    )}
                    style={{
                      left: bar.x + previewDelta,
                      width: bar.width,
                      top: SCALE_HEIGHT + bar.row * ROW_HEIGHT + 3,
                      height: ROW_HEIGHT - 6,
                    }}
                    title={`${bar.card.title}\n${bar.startISO.slice(0, 10)} → ${bar.endISO.slice(0, 10)}`}
                  >
                    <span className="truncate">{bar.card.title}</span>
                  </button>
                );
              })}

              {/* Dependency arrows — purely decorative, parent aria-hidden */}
              <svg
                className="pointer-events-none absolute inset-0"
                width={layout.width}
                height={chartHeight + SCALE_HEIGHT}
                aria-hidden
              >
                <title>Dependency arrows</title>
                <defs>
                  <marker
                    id="gantt-arrow-head"
                    viewBox="0 0 10 10"
                    refX="9"
                    refY="5"
                    markerWidth="6"
                    markerHeight="6"
                    orient="auto-start-reverse"
                  >
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
                  </marker>
                </defs>
                <g transform={`translate(0, ${SCALE_HEIGHT})`} className="text-foreground/40">
                  {layout.arrows.map((arrow) => {
                    const onCritical =
                      critical.has(arrow.fromCardId) && critical.has(arrow.toCardId);
                    return (
                      <path
                        key={`${arrow.fromCardId}->${arrow.toCardId}`}
                        d={arrow.d}
                        fill="none"
                        stroke={onCritical ? '#e11d48' : 'currentColor'}
                        strokeWidth={onCritical ? 2 : 1.25}
                        markerEnd="url(#gantt-arrow-head)"
                      />
                    );
                  })}
                </g>
              </svg>

              {/* Today line — decorative vertical strip */}
              {todayX !== null && (
                <div
                  className="pointer-events-none absolute top-0 z-20 w-px bg-amber-500"
                  style={{ left: todayX, height: chartHeight + SCALE_HEIGHT }}
                  title="Today"
                  aria-hidden
                />
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function ZoomControls({
  value,
  onChange,
}: {
  value: GanttScale;
  onChange: (v: GanttScale) => void;
}) {
  return (
    <div className="inline-flex rounded border border-border bg-background text-xs">
      {(['day', 'week', 'month'] as const).map((s) => (
        <button
          type="button"
          key={s}
          onClick={() => onChange(s)}
          className={cn(
            'h-7 px-2 capitalize transition-colors',
            s === value
              ? 'bg-foreground/[0.06] text-foreground'
              : 'text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground',
            s !== 'month' && 'border-r border-border',
          )}
        >
          {s}
        </button>
      ))}
    </div>
  );
}
